import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref } from 'vue'

import sync from '@/sync/service'
import type { KitchenOrder, KitchenOrderMapped, Tab } from '@/types'
import { mapKitchenOrder } from '@/utils/mappers'
import { normalizeTabs } from '@/utils/tabs.utils'

export const useKitchenOrderStore = defineStore('kitchenOrders', () => {
  const kitchenOrders = ref<Record<string, KitchenOrder>>({})

  const mappedKitchenOrders = computed<Record<string, KitchenOrderMapped>>(() => {
    const result: Record<string, KitchenOrderMapped> = {}

    for (const [orderId, order] of Object.entries(kitchenOrders.value)) {
      result[orderId] = mapKitchenOrder(order)
    }

    return result
  })

  async function refreshCurrentTabs(tabsPayload: Tab[]): Promise<void> {
    const tabsData = normalizeTabs(tabsPayload)
    kitchenOrders.value = (tabsData && tabsData.kitchenOrders) || {}
  }

  function updateKitchenOrderStatus(orderId: string, status: KitchenOrder['status']) {
    sync.record('kitchenOrderStatusUpdated', { orderId, status })
  }

  function updateKitchenProductStatus(
    orderId: string,
    productId: string,
    status: 'pending' | 'ongoing' | 'completed' | 'cancelled' | null
  ) {
    const order = kitchenOrders.value[orderId]
    if (!order) return

    sync.record('kitchenProductStatusUpdated', { orderId, productId, status })

    if (status === 'ongoing' && (!order.status || order.status === 'pending')) {
      updateKitchenOrderStatus(orderId, 'ongoing')
    }

    if (status === 'completed') {
      const allProducts = order.versions.flatMap(v => v.products)
      const allCompleted = allProducts.every(p => p.id === productId || p.status === 'completed')

      if (allCompleted) {
        updateKitchenOrderStatus(orderId, 'completed')
      }
    }
  }

  return {
    kitchenOrders,
    mappedKitchenOrders,
    updateKitchenOrderStatus,
    updateKitchenProductStatus,
    refreshCurrentTabs
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useKitchenOrderStore, import.meta.hot))
}
